<template>
    <q-card flat square bordered>
        <q-item class="text-white bg-deep-orange" v-if="workOrder.differsFromInvoiced">
            <q-item-section avatar>
                <q-icon name="mdi-file-alert" />
            </q-item-section>
            <q-item-section>
                <q-item-label class="text-bold">
                    Warning
                </q-item-label>
                <q-item-label>
                    This work order has changed since it was invoiced!
                </q-item-label>
            </q-item-section>
            <q-item-section side>
                <q-btn
                    padding="3px"
                    icon="mdi-file-document-refresh"
                    color="amber-10"
                    @click="$emit('regenerate-invoice')"
                >
                    <q-tooltip>
                        Regenerate invoice
                    </q-tooltip>
                </q-btn>
            </q-item-section>
        </q-item>

        <q-expansion-item
            dense
            header-class="bg-primary text-white"
            expand-icon-class="text-white"
            expand-separator
            :model-value="true"
        >
            <template #header={expanded}>
                <q-separator 
                    vertical
                    style="width: 5px;position: absolute;left: 0;top: 0;bottom: 0;"
                    :color="expanded ? ($q.dark.isActive ? 'dark' : 'white') : 'transparent'"
                />
                <q-item-section side>
                    <q-item-label 
                        :class="{
                            'text-bold': 1,
                            'text-grey-3': $q.dark.isActive,
                            'text-white': !$q.dark.isActive,
                        }"
                    >
                        Work Order #{{ workOrder.id }}
                    </q-item-label>
                </q-item-section>

                <q-item-section />

                <q-item-section side>
                    <q-badge v-if="workOrder.id > 0"
                        :label="_status(workOrder.status)"
                        :class="workOrder.status === 'draft' ? 'bg-grey' : ''"
                        :color="_status(workOrder.status, true).replace('primary', 'secondary')"
                        @click.stop="editable ? openStatusMenu : undefined"
                        style="user-select: none;"
                    >
                        <q-menu v-if="editable" square v-model="statusMenu.open">
                            <q-list separator dense>
                                <q-item 
                                    v-for="(status, index) in statusMenu.options" 
                                    :key="index"
                                    :label="status.text"
                                    :value="status.value"
                                    tag="label"
                                    v-ripple
                                >
                                    <q-item-section avatar>
                                        <q-radio 
                                            v-model="workOrder.status"
                                            :val="status.value" 
                                            size="xs"
                                            :color="status.value === 'pending' ? 'secondary' : _statusColor(status.value)" 
                                        />
                                    </q-item-section>
                                    <q-item-section side>
                                        <q-badge
                                            :label="status.text"
                                            :class="`q-ml-xs q-mr-md ${status.value === 'draft' ? 'bg-grey' : ''}`"
                                            :color="_status(status.value, true).replace('primary', 'secondary')"
                                        />
                                    </q-item-section>
                                </q-item>
                            </q-list>
                        </q-menu>
                    </q-badge>
                </q-item-section>

                <q-item-section 
                    side
                    v-if="['invoiced', 'processed'].includes(workOrder.status) && getUser().role_id < 3" 
                >
                    <q-btn
                        flat
                        padding="3px"
                        :icon="'mdi-lock' + (locked ? '-open-alert' : '')"
                        :color="locked ? 'yellow-12' : 'grey-3'"
                        @click.stop.prevent="editable = (locked = !locked)"
                        size="sm"
                    >
                        <q-tooltip>
                            {{ !locked ? 'Unlock' : 'Lock' }} work order
                        </q-tooltip>
                    </q-btn>
                </q-item-section>
            </template>

            <q-list dense separator>
                <q-expansion-item dense expand-icon-class="hidden">
                    <template #header="{ expanded }">
                        <q-item-section 
                            :class="{
                                'text-underline': 1,
                                'text-grey-7': !$q.dark.isActive,
                                'text-grey-2': $q.dark.isActive,
                            }"
                        >
                            Location
                        </q-item-section>
                        <q-item-section v-if="editable || locked" side>
                            <q-btn
                                size="sm"
                                padding="3px"
                                @click.stop.prevent="swapLocation = true"
                                icon="mdi-swap-horizontal"
                                color="purple-12"
                                outline
                                v-if="false"
                            >
                                <q-tooltip class="bg-purple-13">
                                    Swap location
                                </q-tooltip>
                            </q-btn>
                        </q-item-section>
                        <q-item-section side>
                            <q-item-label class="text-bold text-light-blue-13">
                                <q-icon size="1rem" style="transition: transform 0.15s ease;" name="mdi-chevron-down" :class="{'rotate-180':expanded}" />
                            </q-item-label>
                        </q-item-section>
                        <q-item-section
                            style="padding-left: 2.5px !important;"
                            side
                        >
                            <q-item-label @click.stop class="text-bold text-underline cursor-pointer text-light-blue-13">
                                {{ workOrder.contact?.org_name ?? '*Unknown*' }}

                                <q-menu>
                                    <q-list separator dense>
                                        <q-item 
                                            clickable
                                            @click.stop.prevent="swapLocation = true"
                                            v-close-popup
                                        >
                                            <q-item-section side>
                                                <q-item-label>
                                                    <q-icon size="sm" name="mdi-swap-horizontal" />
                                                </q-item-label>
                                            </q-item-section>
                                            <q-item-section>
                                                <q-item-label>
                                                    Swap Location
                                                </q-item-label>
                                            </q-item-section>
                                        </q-item>
                                        <q-item 
                                            clickable
                                            v-close-popup
                                            @click.stop.prevent="$inertia.visit(route('customer.show', workOrder.customer_id))"
                                        >
                                            <q-item-section side>
                                                <q-item-label>
                                                    <q-icon size="sm" name="mdi-open-in-app" />
                                                </q-item-label>
                                            </q-item-section>
                                            <q-item-section>
                                                <q-item-label>
                                                    Go to customer
                                                </q-item-label>
                                            </q-item-section>
                                        </q-item>
                                    </q-list>
                                </q-menu>
                            </q-item-label>
                        </q-item-section>
                    </template>

                    <ContactViewModel
                        v-if="workOrder.contact"
                        :contact="workOrder.contact" 
                    />
                </q-expansion-item>

                <q-item>
                    <q-item-section 
                        :class="{
                            'text-grey-7': !$q.dark.isActive,
                            'text-grey-2': $q.dark.isActive,
                        }"
                    >Created By</q-item-section>
                    <q-item-section
                        side
                        :class="{
                            'text-grey-6': (!workOrder.creator && !getUser().name),
                            'text-bold text-light-blue-13 text-underline cursor-pointer': workOrder.creator ?? getUser().name ?? false,
                        }"
                        @click.stop="(workOrder.creator || getUser()) ? windowOpen('/user/' + (workOrder.user_id ?? getUser().id)) : undefined"
                    >
                        <q-item-label>
                            <Avatar
                                size="1.25rem"
                                class="q-mr-xs"
                                :user="workOrder.creator ?? getUser() ?? false"
                                v-if="!isNew"
                            /> 
                            {{ workOrder.creator?.name ?? getUser().name ?? '*Unknown*' }}
                            <q-icon name="mdi-open-in-new" size="0.8rem" />
                        </q-item-label>
                    </q-item-section>
                </q-item>
                <q-item v-if="workOrder.completed_at">
                    <q-item-section 
                        :class="{
                            'text-grey-7': !$q.dark.isActive,
                            'text-grey-2': $q.dark.isActive,
                        }"
                    >Completed At</q-item-section>
                    <q-item-section side class="text-bold">{{ format(workOrder.completed_at) }}</q-item-section>
                </q-item>
                <q-item>
                    <q-item-section 
                        :class="{
                            'text-grey-7': !$q.dark.isActive,
                            'text-grey-2': $q.dark.isActive,
                        }"
                    >
                        <q-item-label>
                            Work Date
                            <q-icon 
                                v-if="dateMismatch && (editable || locked)"
                                size="xs"
                                name="mdi-alert"
                                color="deep-orange"
                            >
                                <q-tooltip class="bg-deep-orange">
                                    Date mismatch!
                                </q-tooltip>
                            </q-icon>
                        </q-item-label>
                    </q-item-section>
                    <q-item-section side class="text-light-blue-13 text-bold">
                        <q-item-label 
                            @click.stop.prevent
                            :class="{'text-underline cursor-pointer': (editable || locked)}"
                        >
                            <q-icon name="mdi-calendar-edit" v-if="(editable || locked)" /> 
                            {{ formatTo('MMM do uuuu', workOrder.work_start_at) }}
                            
                            <q-popup-proxy v-if="(editable || locked)" @before-show="beforePopup" cover transition-show="scale" transition-hide="scale">
                                <q-date minimal v-model="dateProxy" mask="YYYY-MM-DD HH:mm">
                                    <div class="row items-center justify-end q-gutter-sm">
                                        <q-btn label="Cancel" color="primary" flat v-close-popup />
                                        <q-btn label="OK" color="primary" @click="changeDate" flat v-close-popup />
                                    </div>
                                </q-date>
                            </q-popup-proxy>
                        </q-item-label>
                    </q-item-section>
                </q-item>
                <q-item>
                    <q-item-section 
                        :class="{
                            'text-grey-7': !$q.dark.isActive,
                            'text-grey-2': $q.dark.isActive,
                        }"
                    >
                        <q-item-label>
                            Created
                            <q-icon 
                                v-if="dateMismatch && (editable || locked)"
                                size="xs"
                                name="mdi-alert"
                                color="deep-orange"
                            >
                                <q-tooltip class="bg-deep-orange">
                                    Date mismatch!
                                </q-tooltip>
                            </q-icon>
                        </q-item-label>
                    </q-item-section>
                    <q-item-section side class="text-bold">
                        {{ format(workOrder.created_at, true) }}
                    </q-item-section>
                </q-item>
                <q-item v-if="workOrder.updated_at != workOrder.created_at">
                    <q-item-section 
                        :class="{
                            'text-grey-7': !$q.dark.isActive,
                            'text-grey-2': $q.dark.isActive,
                        }"
                    >Updated</q-item-section>
                    <q-item-section side class="text-bold">
                        {{ format(workOrder.updated_at) }}
                    </q-item-section>
                </q-item>

                <q-item v-if="workOrder.total ?? false" dense>
                    <q-item-section>
                        <q-item-label caption>
                            <q-badge color="purple-12" :outline="$q.dark.isActive">
                                <q-icon name="mdi-account-group" class="q-mr-xs" />
                                {{ workOrder.total.laborers }}
                            </q-badge>
                            Worker{{ workOrder.total.laborers !=1 ? 's' : '' }}
                        </q-item-label>
                        <q-item-label caption>
                            <q-badge color="secondary" :outline="$q.dark.isActive">
                                <q-icon name="mdi-hammer-wrench" class="q-mr-xs" />
                                {{ workOrder.total.work_items }}
                            </q-badge>
                            Work Item{{ workOrder.total.work_items !=1 ? 's' : '' }}
                        </q-item-label>
                    </q-item-section>
                    <q-item-section class="text-right">
                        <q-item-label caption>
                            Total Expenses 
                            <q-badge :color="!$q.dark.isActive ? 'secondary' : 'white'" :outline="$q.dark.isActive">
                                {{ workOrder.total.expenses == null ? '$0.00' : toUSD(workOrder.total.expenses) }}
                                
                                <q-tooltip>
                                    Total amount paid to workers
                                </q-tooltip>
                            </q-badge>
                        </q-item-label>
                        <q-item-label caption>
                            Total Bill 
                            <q-badge
                                :outline="$q.dark.isActive"
                                color="green-13"
                                :text-color="isDarkRadial('#00e676', 'white', 'black')"
                            >
                                {{ workOrder.total.gross == null ? '$0.00' : toUSD(workOrder.total.gross) }}
                                
                                <q-tooltip class="bg-positive text-white">
                                    Total amount billed to the customers
                                </q-tooltip>
                            </q-badge>
                        </q-item-label>
                    </q-item-section>
                </q-item>
            </q-list>
        </q-expansion-item>

        <q-card-actions class="q-px-md" v-if="showCardActions" :align="$q.screen.lt.md ? 'center' : 'right'">
            <!-- <q-btn
                no-caps
                size="sm"
                color="amber-10"
                icon="mdi-file-document-refresh"
                v-if="showRegenerateAction"
            >
                <q-tooltip class="bg-amber-10 text-white">
                    <q-icon name="mdi-file-document-refresh" />
                    Regenerate invoice
                </q-tooltip>
            </q-btn> -->

            <q-btn
                no-caps
                size="sm"
                color="positive"
                icon="mdi-content-save-all"
                @click="$emit('save')"
                v-if="showSaveAction"
            >
                <q-tooltip class="bg-positive text-white">
                    <q-icon name="mdi-content-save-all" />
                    Save modifications
                </q-tooltip>
            </q-btn>

            <q-btn
                v-if="showCancelAction"
                no-caps
                size="sm"
                color="deep-orange"
                icon="mdi-file-clock"
                @click="$emit('cancel')"
            >
                <q-tooltip class="bg-deep-orange text-white">
                    <q-icon name="mdi-file-clock" />
                    Cancel modifications
                </q-tooltip>
            </q-btn>

            <q-btn
                v-if="showEditAction"
                no-caps
                size="sm"
                color="secondary"
                icon="mdi-file-edit"
                @click="$emit('edit')"
                outline
            >
                <q-tooltip class="bg-primary text-white">
                    <q-icon name="mdi-file-edit" />
                    Edit work items
                </q-tooltip>
            </q-btn>

            <q-btn
                no-caps
                size="sm"
                color="red-13"
                icon="mdi-trash-can"
                @click="$emit('delete')"
                v-if="showDeleteAction"
                outline
            >
                <q-tooltip class="bg-negative text-white">
                    <q-icon name="mdi-trash-can" />
                    Delete this work order
                </q-tooltip>
            </q-btn>
        </q-card-actions>
    </q-card>
    
    <SwapLocation
        v-model="swapLocation"
        v-model:customer-id="workOrder.customer_id"
        :contact="workOrder.contact"
        @update:contact="updateContact"
    />
</template>

<script>
import Avatar from '@/Components/Avatar.vue';
import ContactViewModel from '@/Components/Models/Contact/ViewModel.vue';

import SwapLocation from './SwapDialog/SwapLocationDialog.vue';

export default {
    name: 'WorkOrderDetails',
    emits: ['edit', 'save', 'delete', 'create', 'cancel', 'update:isLocked', 'regenerate-invoice'],
    props: {
        workOrder: {
            type: Object,
            required: true
        },
        editable: {
            type: Boolean,
            default: false
        },
        editing: {
            type: Boolean,
            default: false
        },
        isLocked: {
            type: Boolean,
            default: false
        },
        isModified: {
            type: Boolean,
            default: false
        },
        isNew: {
            type: Boolean,
            default: false
        },
        newWorkItemsLength: {
            type: Number,
            default: 0
        },
        workItemsLength: {
            type: Number,
            default: 0
        },
        loading: {
            type: Boolean,
            default: false
        },
    },
    components: {
        Avatar,
        ContactViewModel,
        SwapLocation,
    },
    data() {
        return {
            // isLocked: this.lockState,
            swapLocation: false,
            dateProxy: this.workOrder.work_start_at,
            statusMenu: {
                open: false,
                options: [
                    { text: 'Draft', value: 'draft', selected: false },
                    { text: 'Pending', value: 'pending', selected: false },
                    {
                        text: 'In Progress',
                        value: 'in_progress',
                        selected: false,
                    },
                    { text: 'Completed', value: 'completed', selected: false },
                ],
            },
        };
    },
    methods: {
        changeDate() {
            let temp = {...this.workOrder};

            temp.work_start_at = this.dateProxy;

            this.$emit('update:work-order', temp);

            // this.$emit('save', true);
        },
        beforePopup() {
            this.dateProxy = this.workOrder.work_start_at;
        },
        openStatusMenu() {
            if (this.workOrder.id < 1) return;

            this.statusMenu.open = this.editableStatus(this.workOrder.status);
        },
        updateContact(contact) {
            this.workOrder.contact_id = contact.id;
            this.workOrder.contact = contact;

            // this.$emit('save', true);
            // Close dialog
            this.swapLocation = false;
        },
        saveModifications() {
            if (this.isModified && ['invoiced', 'processed'].includes(this.workOrder.status) && this.getUser().role_id < 3) {
                if (!confirm('This work order has been invoiced or processed. Are you sure you want to save the modifications?')) {
                    return;
                }
            }

            this.$emit('save');
        },
    },
    computed: {
        locked: {
            get() {
                return this.isLocked;
            },
            set(value, prev) {
                if (!value && (value != prev) && this.isModified) {
                    if (!confirm('This work order has been modified. Locking it will discard the changes. Are you sure you want to proceed?')) {
                        return;
                    }

                    this.$emit('cancel');
                }
                
                this.$emit('update:isLocked', value);
            },
        },
        showCardActions() {
            return (
                this.showRegenerateAction ||
                this.showSaveAction ||
                this.showCancelAction ||
                this.showEditAction ||
                this.showDeleteAction
            );
        },
        dateMismatch() {
            const startDate = this.formatTo('yyyy-MM-dd', this.workOrder.work_start_at);
            const createdDate = this.formatTo('yyyy-MM-dd', this.workOrder.created_at);

            return startDate !== createdDate;
        },
        showRegenerateAction() {
            return this.workOrder.invoice_id && this.workOrder.isDirty;
        },
        showSaveAction() {
            return (this.editing || this.workOrder.isDirty || this.isModified) && !this.isNew;
        },
        showCancelAction() {
            return this.editing || this.workOrder.isDirty || this.newWorkItemsLength > 0;
        },
        showEditAction() {
            return !this.editing && this.workOrder.id > 0 && (this.locked || this.editable);
        },
        showDeleteAction() {
            return this.workOrder.id > 0 && this.workOrder.status !== 'invoiced';
        },
    },
}
</script>